import { useState } from 'react';
import { useUserSetupStore } from '@sib/auth';
import { useAgentSessionStore } from '../stores';
import { buildAgentIframeKey } from '../utils/agent-iframe-key';

/**
 * Remount key that always tracks the latest mint (`refreshNonce`).
 * Use for long-lived iframes (e.g. document viewer) that must pick up a
 * refreshed agent cookie immediately.
 */
export const useAgentIframeKey = (): string => {
  const organizationId = useUserSetupStore(state => state.organization.id);
  const refreshNonce = useAgentSessionStore(state => state.refreshNonce);

  return buildAgentIframeKey(organizationId, refreshNonce);
};

interface StableIframeSession {
  orgId: string;
  key: string;
}

/**
 * Remount key frozen for the duration of an open surface (e.g. Ask AI modal).
 * Background token refresh still mints a fresh cookie, but does not wipe an
 * in-progress session; the next open uses the latest nonce.
 */
export const useStableAgentIframeKeyWhileOpen = (
  isOpen: boolean
): string | null => {
  const organizationId = useUserSetupStore(state => state.organization.id);
  const refreshNonce = useAgentSessionStore(state => state.refreshNonce);
  const [session, setSession] = useState<StableIframeSession | null>(null);

  if (!isOpen) {
    if (session !== null) {
      setSession(null);
    }
  } else if (session === null || session.orgId !== organizationId) {
    setSession({
      orgId: organizationId,
      key: buildAgentIframeKey(organizationId, refreshNonce),
    });
  }

  return session?.key ?? null;
};
